import { Box, Button, Chip, FormControl, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

export const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: theme.palette.background.contentBackground,
  padding: theme.spacing(1, 2),
  "& .MuiDialogTitle-root": {
    ...theme.typography.h4,
    padding: 0,
  },
}));

export const FormFieldsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  paddingTop: 0,
}));

export const PackageSelectField = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  "& .MuiFormHelperText-root": {
    marginLeft: theme.spacing(1.75),
    marginRight: theme.spacing(1.75),
    color: theme.palette.text.secondary,
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
}));

export const FooterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
}));

export const FooterHelperText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 400,
  fontSize: "0.75rem", // 12px
  lineHeight: 1.66,
  letterSpacing: "0.4px",
  color: theme.palette.text.secondary, // text/secondary
  textAlign: "right",
}));

export const CreateButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 500,
  fontSize: "0.875rem", // 14px
  lineHeight: "24px",
  letterSpacing: "0.4px",
  padding: theme.spacing(0.75, 2),
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  boxShadow: theme.shadows[2],
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

export const ProjectNameField = styled(FormControl)(({ theme }) => ({
  width: "100%",
  "& .MuiInputLabel-asterisk": {
    color: theme.palette.error.main,
  },
  "& .MuiFormHelperText-root": {
    marginLeft: theme.spacing(1.75),
    marginRight: theme.spacing(1.75),
    color: theme.palette.text.secondary,
  },
}));

export const TwoColumnFieldsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  flexWrap: "wrap",
  "& > *": {
    flex: "1 0 180px",
    minWidth: "180px",
  },
}));

export const NetSuiteField = styled(FormControl)(({ theme }) => ({
  "& .MuiInputBase-input::placeholder": {
    color: theme.palette.text.disabled,
  },
}));

export const IsbnField = styled(FormControl)(() => ({
  "& .MuiInputBase-input": {
    fontFamily: "'Roboto', sans-serif",
  },
}));

export const PillsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "wrap",
  alignItems: "flex-start",
}));

export const MetadataPill = styled(Chip)(({ theme }) => ({
  height: "24px",
  maxHeight: "24px",
  minHeight: "24px",
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: "100px",
  backgroundColor: "transparent",
  paddingLeft: "4px",
  paddingRight: "4px",
  paddingTop: "3px",
  paddingBottom: "3px",
  "& .MuiChip-label": {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 400,
    fontSize: "0.75rem", // 12px
    lineHeight: "18px",
    letterSpacing: "0.16px",
    color: theme.palette.text.primary,
    padding: "0 6px",
  },
  "& .MuiChip-icon": {
    width: "18px",
    height: "18px",
    fontSize: "18px",
    color: theme.palette.action.disabled,
    margin: 0,
    marginLeft: "0",
  },
  cursor: "default",
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

export const CreateProofingTitleBtn = styled(Button)(({ theme }) => ({
  background: `
    linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.96)} 0%, ${alpha(theme.palette.primary.main, 0.96)} 100%),
    radial-gradient(50% 50% at 50% 50%, ${alpha(theme.palette.primary.light, 0.2)} 0%, ${alpha(theme.palette.secondary.light, 0.2)} 100%)`,
  backgroundBlendMode: "overlay",
  height: "42px",
  borderRadius: theme.shape.borderRadius,
  paddingRight: theme.spacing(3),
  paddingLeft: theme.spacing(2.5),
  transition: theme.transitions.create(["box-shadow"], {
    duration: theme.transitions.duration.short,
  }),

  "&:hover": {
    boxShadow: theme.shadows[4],
  },

  "& .MuiButton-startIcon": {
    "& > *:nth-of-type(1)": {
      fontSize: "24px",
    },
  },

  "&.Mui-disabled": {
    background: theme.gradients?.primaryDisabled || theme.palette.action.disabledBackground,
  },
}));
